import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { softDelete } from '../utils/softDelete';
import { useEsAdmin } from '../UserContext';
import ConfirmModal from '../components/ConfirmModal';
import ExpedienteDetalle from './ExpedienteDetalle';
import { emitTareaChanged, onTareaChanged } from '../utils/tareasBus';
import { onEntidadChanged } from '../utils/entidadBus';
import { onDataChanged } from '../utils/dataBus';

export default function Asignaciones() {
  const esAdmin = useEsAdmin();
  const [userId, setUserId] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [asignaciones, setAsignaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);

  // Modal nueva/editar asignación
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({ titulo: '', descripcion: '', asignados_a: [], fecha: '' });

  // Asignación abierta (detalle)
  const [asignacionAbierta, setAsignacionAbierta] = useState(null);
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [nuevoItem, setNuevoItem] = useState('');
  const [agregandoItem, setAgregandoItem] = useState(false);

  // Modal tarea libre
  const [modalNuevoItem, setModalNuevoItem] = useState(false);
  const [nuevoItemNotas, setNuevoItemNotas] = useState('');

  // Modal detalle de ítem
  const [itemAbierto, setItemAbierto] = useState(null);
  const [editandoNotasItem, setEditandoNotasItem] = useState(false);
  const [notasItemEdit, setNotasItemEdit] = useState('');

  // Drawer de expediente (desde ítem vinculado)
  const [expedienteDrawerOpen, setExpedienteDrawerOpen] = useState(false);
  const [expedienteDrawerExpId, setExpedienteDrawerExpId] = useState(null);

  // Modal vincular tarea
  const [modalVincular, setModalVincular] = useState(false);
  const [tabVincular, setTabVincular] = useState('clientes');
  const [busquedaVincular, setBusquedaVincular] = useState('');
  const [entidadVincular, setEntidadVincular] = useState(null);
  const [tareaSeleccionadaVincular, setTareaSeleccionadaVincular] = useState(null);
  const [tareasDisponibles, setTareasDisponibles] = useState([]);

  // Todas las entidades para el modal vincular
  const [todasEntidades, setTodasEntidades] = useState({ clientes: [], expedientes: [], tramites: [] });

  // Acordeones — por defecto todo cerrado excepto HOY
  const [cerrados, setCerrados] = useState(new Set());
  const cerradosInicializado = useRef(false);
  const mouseDownPos = useRef(null);

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 3000); }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id || null);
    });
  }, []);

  useEffect(() => {
    if (!userId) return;
    fetchAsignaciones();
    fetchUsuarios();
    fetchTareasDisponibles();
    fetchTodasEntidades();
  }, [userId, esAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mantenerse sincronizado cuando una tarea cambia desde otro lugar (Espacio de trabajo, etc.)
  useEffect(() => onTareaChanged(() => {
    if (!userId) return;
    fetchTareasDisponibles();
    if (asignacionAbierta) fetchItems(asignacionAbierta.id);
  }), [userId, asignacionAbierta]); // eslint-disable-line react-hooks/exhaustive-deps

  // Idem cuando se elimina/edita un expediente o trámite vinculado
  useEffect(() => onEntidadChanged(() => {
    if (!userId) return;
    fetchTareasDisponibles();
    if (asignacionAbierta) fetchItems(asignacionAbierta.id);
  }), [userId, asignacionAbierta]); // eslint-disable-line react-hooks/exhaustive-deps

  // Automático: cualquier escritura sobre tareas, expedientes o trámites
  useEffect(() => onDataChanged(() => {
    if (!userId) return;
    fetchTareasDisponibles();
    if (asignacionAbierta) fetchItems(asignacionAbierta.id);
  }, ['tareas', 'expedientes', 'tramites', 'asignacion_items']), [userId, asignacionAbierta]); // eslint-disable-line react-hooks/exhaustive-deps

  // Inicializar acordeones: cerrar todo excepto HOY (solo una vez al cargar)
  useEffect(() => {
    if (loading || cerradosInicializado.current || asignaciones.length === 0) return;
    cerradosInicializado.current = true;
    const todayStr = new Date().toISOString().split('T')[0];
    const todasFechas = [...new Set(asignaciones.map(a => a.fecha))];
    setCerrados(new Set(todasFechas.filter(f => f !== todayStr)));
  }, [loading, asignaciones]);

  async function fetchUsuarios() {
    const { data } = await supabase
      .from('profiles')
      .select('id, nombre, rol')
      .order('nombre');
    if (!data || data.length === 0) {
      const { data: { session } } = await supabase.auth.getSession();
      setUsuarios(session ? [{ id: session.user.id, nombre: session.user.email?.split('@')[0] || 'Mi usuario', rol: 'admin' }] : []);
    } else {
      setUsuarios(data.map(u => ({ ...u, nombre: u.nombre || u.rol || 'Sin nombre' })));
    }
  }

  async function fetchAsignaciones() {
    setLoading(true);
    if (esAdmin) {
      const { data } = await supabase
        .from('asignaciones')
        .select('*, asignacion_items(id, completado), asignacion_usuarios(usuario_id)')
        .eq('eliminado', false)
        .order('fecha', { ascending: false });
      setAsignaciones(data || []);
    } else {
      const { data: junction, error: jErr } = await supabase
        .from('asignacion_usuarios')
        .select('asignacion_id')
        .eq('usuario_id', userId);

      if (jErr) {
        console.error('Error leyendo asignacion_usuarios:', jErr);
        showToast('Error al cargar asignaciones: ' + jErr.message);
        setAsignaciones([]);
        setLoading(false);
        return;
      }

      if (!junction || junction.length === 0) {
        setAsignaciones([]);
        setLoading(false);
        return;
      }

      const ids = junction.map(r => r.asignacion_id);
      const { data, error: aErr } = await supabase
        .from('asignaciones')
        .select('*, asignacion_items(id, completado), asignacion_usuarios(usuario_id)')
        .in('id', ids)
        .eq('eliminado', false)
        .order('fecha', { ascending: false });

      if (aErr) console.error('Error leyendo asignaciones:', aErr);
      setAsignaciones(data || []);
    }
    setLoading(false);
  }

  async function fetchTodasEntidades() {
    const [
      { data: clientes, error: errC },
      { data: expedientes, error: errE },
      { data: tramites, error: errT },
    ] = await Promise.all([
      supabase.from('clientes').select('id, nombre, apellido').eq('eliminado', false).order('apellido'),
      supabase.from('expedientes').select('id, caratula, numero, expedientes_clientes(clientes(nombre, apellido))').eq('eliminado', false).order('caratula'),
      supabase.from('tramites').select('id, nombre, tramites_clientes(clientes(nombre, apellido))').eq('eliminado', false).order('nombre'),
    ]);
    if (errC) console.error('fetchTodasEntidades — clientes:', errC);
    if (errE) console.error('fetchTodasEntidades — expedientes:', errE);
    if (errT) console.error('fetchTodasEntidades — tramites:', errT);
    setTodasEntidades({
      clientes: clientes || [],
      expedientes: expedientes || [],
      tramites: tramites || [],
    });
  }

  async function fetchTareasDisponibles() {
    const { data, error } = await supabase
      .from('tareas')
      .select('id, titulo, expediente_id, tramite_id, expedientes(id, caratula, numero, expedientes_clientes(cliente_id)), tramites(id, nombre, tramites_clientes(cliente_id))')
      .eq('completada', false)
      .eq('eliminado', false)
      .order('created_at', { ascending: false });
    if (error) console.error('fetchTareasDisponibles:', error);
    setTareasDisponibles(data || []);
  }

  async function fetchItems(asignacionId) {
    setLoadingItems(true);
    const { data } = await supabase
      .from('asignacion_items')
      .select('*, tareas(titulo, expedientes(id, caratula, numero), tramites(id, nombre))')
      .eq('asignacion_id', asignacionId)
      .order('orden', { ascending: true });
    setItems(data || []);
    setLoadingItems(false);
  }

  function abrirAsignacion(a) {
    setAsignacionAbierta(a);
    fetchItems(a.id);
    setNuevoItem('');
  }

  function openNew() {
    setEditingId(null);
    const hoy = new Date().toISOString().split('T')[0];
    setForm({ titulo: '', descripcion: '', asignados_a: [], fecha: hoy });
    setModalOpen(true);
  }

  function openEdit(a) {
    setEditingId(a.id);
    const asignadosActuales = (a.asignacion_usuarios || []).map(u => u.usuario_id);
    setForm({ titulo: a.titulo, descripcion: a.descripcion || '', asignados_a: asignadosActuales, fecha: a.fecha });
    setModalOpen(true);
  }

  async function saveAsignacion() {
    if (!form.titulo.trim()) { showToast('El título es obligatorio'); return; }
    if (form.asignados_a.length === 0) { showToast('Seleccioná al menos un colaborador'); return; }
    if (!form.fecha) { showToast('La fecha es obligatoria'); return; }
    setGuardando(true);

    const payload = {
      titulo: form.titulo.trim(),
      descripcion: form.descripcion.trim() || null,
      fecha: form.fecha,
      creado_por: userId,
    };

    let asignacionId = editingId;

    if (editingId) {
      const { error } = await supabase.from('asignaciones').update(payload).eq('id', editingId);
      if (error) { showToast('Error al guardar: ' + error.message); setGuardando(false); return; }
    } else {
      const { data, error } = await supabase.from('asignaciones').insert(payload).select('id').single();
      if (error) { showToast('Error al guardar: ' + error.message); setGuardando(false); return; }
      asignacionId = data.id;
    }

    await supabase.from('asignacion_usuarios').delete().eq('asignacion_id', asignacionId);
    const junctionRows = form.asignados_a.map(uid => ({ asignacion_id: asignacionId, usuario_id: uid }));
    const { error: jError } = await supabase.from('asignacion_usuarios').insert(junctionRows);
    if (jError) { showToast('Error al asignar usuarios: ' + jError.message); setGuardando(false); return; }

    showToast(editingId ? 'Asignación actualizada ✓' : 'Asignación creada ✓');
    setGuardando(false);
    setModalOpen(false);
    fetchAsignaciones();
  }

  async function deleteAsignacion(id) {
    setConfirm({
      titulo: 'Eliminar asignación',
      mensaje: '¿Eliminar esta asignación y todos sus ítems?',
      onConfirmar: async () => {
        setConfirm(null);
        await softDelete('asignaciones', id);
        if (asignacionAbierta?.id === id) setAsignacionAbierta(null);
        fetchAsignaciones();
        showToast('Eliminada');
      }
    });
  }

  async function agregarItemLibre() {
    if (!nuevoItem.trim()) return;
    setAgregandoItem(true);
    const maxOrden = items.length > 0 ? Math.max(...items.map(i => i.orden)) + 1 : 0;
    const { error } = await supabase.from('asignacion_items').insert({
      asignacion_id: asignacionAbierta.id,
      descripcion: nuevoItem.trim(),
      notas: nuevoItemNotas.trim() || null,
      orden: maxOrden,
    });
    if (error) {
      showToast('Error al agregar: ' + error.message);
      setAgregandoItem(false);
      return;
    }
    setNuevoItem('');
    setNuevoItemNotas('');
    setModalNuevoItem(false);
    setAgregandoItem(false);
    fetchItems(asignacionAbierta.id);
    fetchAsignaciones();
  }

  async function agregarItemVinculado() {
    if (!tareaSeleccionadaVincular) return;
    setAgregandoItem(true);
    const maxOrden = items.length > 0 ? Math.max(...items.map(i => i.orden)) + 1 : 0;
    await supabase.from('asignacion_items').insert({
      asignacion_id: asignacionAbierta.id,
      descripcion: tareaSeleccionadaVincular.titulo,
      tarea_id: tareaSeleccionadaVincular.id,
      orden: maxOrden,
    });
    setTareaSeleccionadaVincular(null);
    setBusquedaVincular('');
    setModalVincular(false);
    setAgregandoItem(false);
    fetchItems(asignacionAbierta.id);
    fetchAsignaciones();
  }

  async function toggleItem(item) {
    const completado = !item.completado;
    await supabase.from('asignacion_items').update({
      completado,
      completado_en: completado ? new Date().toISOString() : null,
    }).eq('id', item.id);
    if (completado && item.tarea_id) {
      await supabase.from('tareas').update({ completada: true, completada_en: new Date().toISOString() }).eq('id', item.tarea_id);
      emitTareaChanged({ id: item.tarea_id, completada: true });
    }
    fetchItems(asignacionAbierta.id);
    fetchAsignaciones();
  }

  async function deleteItem(id) {
    await softDelete('asignacion_items', id);
    fetchItems(asignacionAbierta.id);
    fetchAsignaciones();
  }

  async function guardarNotasItem() {
    const notas = notasItemEdit.trim() || null;
    await supabase.from('asignacion_items').update({ notas }).eq('id', itemAbierto.id);
    setItemAbierto(prev => ({ ...prev, notas }));
    setEditandoNotasItem(false);
    fetchItems(asignacionAbierta.id);
  }

  const nombresAsignados = (asignacion_usuarios_arr) => {
    if (!asignacion_usuarios_arr || asignacion_usuarios_arr.length === 0) return 'Sin asignar';
    return asignacion_usuarios_arr
      .map(u => usuarios.find(usr => usr.id === u.usuario_id)?.nombre || 'Usuario')
      .join(', ');
  };

  // Helper: armar string de clientes de un expediente o trámite
  const nombresClientes = (junctionRows) => {
    if (!junctionRows || junctionRows.length === 0) return null;
    return junctionRows
      .map(r => [r.clientes?.apellido, r.clientes?.nombre].filter(Boolean).join(', '))
      .filter(Boolean)
      .join(' / ');
  };

  // Columna izquierda: TODAS las entidades del sistema, filtradas por búsqueda
  const entidadesFiltradas = (() => {
    const busq = busquedaVincular.toLowerCase().trim();
    let lista = [];
    if (tabVincular === 'clientes') {
      lista = todasEntidades.clientes.map(c => ({ id: c.id, nombre: [c.apellido, c.nombre].filter(Boolean).join(', ') }));
    } else if (tabVincular === 'expedientes') {
      lista = todasEntidades.expedientes.map(e => {
        const clientes = nombresClientes(e.expedientes_clientes);
        const partes = [e.numero ? `Nro. ${e.numero}` : null, clientes].filter(Boolean).join(' · ');
        return { id: e.id, nombre: e.caratula || 'Sin carátula', sub: partes || null, clientes };
      });
    } else {
      lista = todasEntidades.tramites.map(t => {
        const clientes = nombresClientes(t.tramites_clientes);
        return { id: t.id, nombre: t.nombre || 'Sin nombre', sub: clientes || null, clientes };
      });
    }
    return lista.filter(e =>
      !busq ||
      e.nombre.toLowerCase().includes(busq) ||
      e.sub?.toLowerCase().includes(busq)
    );
  })();

  // Columna derecha: tareas pendientes de la entidad seleccionada
  const tareasEntidad = entidadVincular ? tareasDisponibles.filter(t => {
    if (tabVincular === 'clientes') {
      const enExp = t.expedientes?.expedientes_clientes?.some(ec => ec.cliente_id === entidadVincular.id);
      const enTram = t.tramites?.tramites_clientes?.some(tc => tc.cliente_id === entidadVincular.id);
      return enExp || enTram;
    } else if (tabVincular === 'expedientes') {
      return t.expediente_id === entidadVincular.id;
    } else {
      return t.tramite_id === entidadVincular.id;
    }
  }) : [];

  // Agrupar por fecha
  const grupos = {};
  asignaciones.forEach(a => {
    const key = a.fecha;
    if (!grupos[key]) grupos[key] = [];
    grupos[key].push(a);
  });
  const fechasOrdenadas = Object.keys(grupos).sort((a, b) => b.localeCompare(a));

  const formatFecha = (key) =>
    new Date(key + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const estadoAsignacion = (a) => {
    const total = a.asignacion_items?.length || 0;
    const completados = a.asignacion_items?.filter(i => i.completado).length || 0;
    const cnt = total > 0 ? ` (${completados}/${total})` : '';
    if (total === 0) return { label: 'Sin ítems', color: 'var(--text4)', bg: 'var(--surface2)' };
    if (completados === total) return { label: `Completada${cnt}`, color: '#10b981', bg: '#d1fae5' };
    if (completados > 0) return { label: `En progreso${cnt}`, color: '#f59e0b', bg: '#fef3c7' };
    return { label: `Pendiente${cnt}`, color: '#ef4444', bg: '#fee2e2' };
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="page">
      <div className="page-topbar">
        <span className="page-title">Asignación de tareas</span>
        {esAdmin && <button className="btn btn-primary" onClick={openNew}>+ Nueva asignación</button>}
      </div>

      {/* DRAWER DETALLE */}
      {asignacionAbierta && (
        <>
          <div className="drawer-overlay" onMouseDown={() => setAsignacionAbierta(null)} />
          <div className="drawer">
            <div className="drawer-header">
              <div className="drawer-header-top">
                <div style={{ flex: 1 }}>
                  <div className="drawer-num">
                    {new Date(asignacionAbierta.fecha + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                  <div className="drawer-title">{asignacionAbierta.titulo}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 10 }}>
                    {esAdmin && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, color: 'var(--text4)', minWidth: 84, flexShrink: 0 }}>Asignado a:</span>
                        <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>{nombresAsignados(asignacionAbierta.asignacion_usuarios)}</span>
                      </div>
                    )}
                    {(() => {
                      const estado = estadoAsignacion(asignacionAbierta);
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 11, color: 'var(--text4)', minWidth: 84, flexShrink: 0 }}>Estado:</span>
                          <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 10, background: estado.bg, color: estado.color, fontWeight: 600 }}>{estado.label}</span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <button className="modal-close" onClick={() => setAsignacionAbierta(null)}>✕</button>
              </div>
              {asignacionAbierta.descripcion && (
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 10, padding: '8px 12px', background: 'var(--surface2)', borderRadius: 7 }}>
                  {asignacionAbierta.descripcion}
                </div>
              )}
              {esAdmin && (
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button className="btn btn-primary" onClick={() => { setAsignacionAbierta(null); openEdit(asignacionAbierta); }}>
                    EDITAR ASIGNACIÓN
                  </button>
                  <button className="btn btn-ghost" style={{ color: '#ef4444' }} onClick={() => deleteAsignacion(asignacionAbierta.id)}>
                    Eliminar
                  </button>
                </div>
              )}
            </div>

            <div className="drawer-body">

              {/* Botones para agregar (solo admin) — arriba del listado */}
              {esAdmin && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
                  <button
                    onClick={() => { setModalVincular(true); setBusquedaVincular(''); setTareaSeleccionadaVincular(null); }}
                    className="btn btn-primary"
                    style={{ flex: 1, padding: '10px 12px', height: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
                  >
                    <span style={{ fontSize: 11 }}>+ Agregar</span>
                    <span style={{ fontSize: 12, fontWeight: 800, textDecoration: 'underline' }}>TAREA VINCULADA</span>
                    <span style={{ fontSize: 11 }}>a Expediente/Trámite</span>
                  </button>
                  <button
                    onClick={() => { setModalNuevoItem(true); setNuevoItem(''); }}
                    className="btn btn-primary"
                    style={{ flex: 1, padding: '10px 12px', height: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
                  >
                    <span style={{ fontSize: 11 }}>+ Agregar</span>
                    <span style={{ fontSize: 12, fontWeight: 800, textDecoration: 'underline' }}>TAREA NO VINCULADA</span>
                    <span style={{ fontSize: 11 }}>a Expedientes/Trámites</span>
                  </button>
                </div>
              )}

              {/* Listado */}
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text4)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Listado de tareas asignadas:
              </div>
              {loadingItems ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text4)', fontSize: 13 }}>Cargando...</div>
              ) : items.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', color: 'var(--text4)', fontSize: 13 }}>Sin ítems todavía</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {items.map(item => (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 0', borderBottom: '1px solid var(--border2)', background: item.completado ? '#f0fdf4' : 'transparent' }}>
                      <input type="checkbox" checked={!!item.completado} onChange={() => toggleItem(item)} style={{ marginTop: 2, cursor: 'pointer', flexShrink: 0, width: 16, height: 16 }} />
                      <div
                        style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                        onClick={() => setItemAbierto(item)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 13, color: 'var(--text)', textDecoration: item.completado ? 'line-through' : 'none', opacity: item.completado ? 0.6 : 1, lineHeight: 1.4 }}>
                            {item.descripcion}
                          </span>
                          {item.notas && (
                            <span style={{ fontSize: 10, color: 'var(--blue)', background: 'var(--blue-light)', border: '1px solid var(--blue)', borderRadius: 4, padding: '1px 6px', fontWeight: 500, cursor: 'pointer' }}>
                              Ver notas
                            </span>
                          )}
                        </div>
                        {item.tareas?.expedientes && <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>📁 {item.tareas.expedientes.caratula?.substring(0, 40)}</div>}
                        {item.tareas?.tramites && <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>📋 {item.tareas.tramites.nombre?.substring(0, 40)}</div>}
                        {item.completado && item.completado_en && (
                          <div style={{ fontSize: 10, color: '#10b981', marginTop: 2 }}>✓ {new Date(item.completado_en).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</div>
                        )}
                      </div>
                      {item.tarea_id && <span style={{ fontSize: 9, color: 'var(--text4)', background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: 4, padding: '1px 5px', flexShrink: 0, marginTop: 2 }}>vinculada</span>}
                      {esAdmin && <button className="btn btn-danger btn-xs" onClick={() => deleteItem(item.id)} style={{ flexShrink: 0 }}>🗑</button>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <div className="page-content">
        <div style={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text4)' }}>Cargando...</div>
          ) : asignaciones.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📋</div>
              <div className="empty-state-title">{esAdmin ? 'No hay asignaciones aún' : 'No tenés tareas asignadas'}</div>
              {esAdmin && <div className="empty-state-sub">Creá una nueva asignación para empezar</div>}
            </div>
          ) : (
            <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {fechasOrdenadas.map(fechaKey => {
                const cerrado = cerrados.has(fechaKey);
                const esHoy = fechaKey === today;
                const esPasado = fechaKey < today;
                const grp = grupos[fechaKey];
                const totalAsignaciones = grp.length;
                const totalTareas = grp.reduce((s, a) => s + (a.asignacion_items?.length || 0), 0);
                const totalCompletadas = grp.reduce((s, a) => s + (a.asignacion_items?.filter(i => i.completado).length || 0), 0);
                const todasCompletas = totalTareas > 0 && totalCompletadas === totalTareas;
                return (
                  <div key={fechaKey} style={{ border: `1px solid ${esHoy ? 'var(--blue)' : 'var(--border2)'}`, borderRadius: 10, overflow: 'hidden' }}>
                    <div
                      onClick={() => setCerrados(prev => { const next = new Set(prev); next.has(fechaKey) ? next.delete(fechaKey) : next.add(fechaKey); return next; })}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: esHoy ? 'var(--blue-light)' : 'var(--surface2)', cursor: 'pointer', userSelect: 'none', gap: 12, flexWrap: 'wrap' }}
                    >
                      {/* Fecha */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        {esHoy && <span style={{ fontSize: 10, background: 'var(--blue)', color: '#fff', borderRadius: 4, padding: '1px 6px', fontWeight: 700 }}>HOY</span>}
                        <span style={{ fontSize: 13, fontWeight: 600, color: esPasado && !esHoy ? 'var(--text4)' : 'var(--text)', textTransform: 'capitalize' }}>{formatFecha(fechaKey)}</span>
                      </div>
                      {/* Stats */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '2px 9px', whiteSpace: 'nowrap' }}>
                          Asignaciones: {totalAsignaciones}
                        </span>
                        {totalTareas > 0 && (
                          <>
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#c2410c', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, padding: '2px 9px', whiteSpace: 'nowrap' }}>
                              Tareas: {totalTareas}
                            </span>
                            <span style={{
                              fontSize: 11, fontWeight: 600, borderRadius: 6, padding: '2px 9px', whiteSpace: 'nowrap',
                              background: todasCompletas ? '#d1fae5' : totalCompletadas > 0 ? '#fef3c7' : '#fee2e2',
                              color: todasCompletas ? '#059669' : totalCompletadas > 0 ? '#b45309' : '#dc2626',
                              border: `1px solid ${todasCompletas ? '#6ee7b7' : totalCompletadas > 0 ? '#fde68a' : '#fca5a5'}`,
                            }}>
                              {todasCompletas ? '✓ ' : ''}Completadas: {totalCompletadas}/{totalTareas}
                            </span>
                          </>
                        )}
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--text4)', flexShrink: 0 }}>{cerrado ? '▶' : '▼'}</span>
                    </div>
                    {!cerrado && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        {grupos[fechaKey].map(a => {
                          const estado = estadoAsignacion(a);
                          return (
                            <div
                              key={a.id}
                              onMouseDown={ev => { mouseDownPos.current = { x: ev.clientX, y: ev.clientY }; }}
                              onClick={ev => {
                                if (mouseDownPos.current) {
                                  const dx = Math.abs(ev.clientX - mouseDownPos.current.x);
                                  const dy = Math.abs(ev.clientY - mouseDownPos.current.y);
                                  if (dx > 5 || dy > 5) return;
                                }
                                abrirAsignacion(a);
                              }}
                              style={{ padding: '12px 16px', borderBottom: '1px solid var(--border2)', background: 'var(--surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{a.titulo}</span>
                                  <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 10, background: estado.bg, color: estado.color, fontWeight: 500, flexShrink: 0 }}>{estado.label}</span>
                                </div>
                                {esAdmin && (
                                  <span style={{ fontSize: 11, color: 'var(--text4)' }}>
                                    → {nombresAsignados(a.asignacion_usuarios)}
                                  </span>
                                )}
                                {a.descripcion && (
                                  <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 3 }}>{a.descripcion}</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* MODAL NUEVA/EDITAR ASIGNACIÓN */}
      {modalOpen && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalOpen(false)}>
          <div className="modal" style={{ width: 480 }}>
            <div className="modal-header">
              <div className="modal-title">{editingId ? 'Editar asignación' : 'Nueva asignación'}</div>
              <button className="modal-close" onClick={() => setModalOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group form-full">
                  <label className="form-label">Título *</label>
                  <input className="form-input" value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} placeholder="Ej: Despacho del jueves" />
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha de Realización *</label>
                  <input className="form-input" type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Asignar a *</label>
                  {usuarios.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#f59e0b', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 7, padding: '8px 12px', lineHeight: 1.5 }}>
                      Para usar esta función necesitás tener al menos un colaborador vinculado. El colaborador aparecerá automáticamente la primera vez que inicie sesión.
                    </div>
                  ) : (
                    <div style={{ border: '1px solid var(--border2)', borderRadius: 8, overflow: 'hidden', maxHeight: 220, overflowY: 'auto' }}>
                      {usuarios.map((u, i) => (
                        <label
                          key={u.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
                            cursor: 'pointer',
                            background: form.asignados_a.includes(u.id) ? 'var(--blue-light)' : 'transparent',
                            borderBottom: i < usuarios.length - 1 ? '1px solid var(--border2)' : 'none',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={form.asignados_a.includes(u.id)}
                            onChange={() => setForm(f => ({
                              ...f,
                              asignados_a: f.asignados_a.includes(u.id)
                                ? f.asignados_a.filter(id => id !== u.id)
                                : [...f.asignados_a, u.id]
                            }))}
                            style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
                          />
                          <div style={{ flex: 1 }}>
                            <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: form.asignados_a.includes(u.id) ? 600 : 400 }}>
                              {u.nombre}
                            </span>
                            <span style={{ fontSize: 11, color: 'var(--text4)', marginLeft: 6 }}>({u.rol})</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Descripción / instrucciones</label>
                  <textarea className="form-textarea" value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} placeholder="Instrucciones generales, notas..." rows={3} />
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
              <div>
                {editingId && (
                  <button className="btn btn-ghost" style={{ color: '#ef4444' }} onClick={() => { setModalOpen(false); deleteAsignacion(editingId); }}>
                    Eliminar asignación
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost" onClick={() => setModalOpen(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={saveAsignacion} disabled={guardando}>
                  {guardando ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL TAREA NO VINCULADA */}
      {modalNuevoItem && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalNuevoItem(false)} style={{ zIndex: 1200 }}>
          <div className="modal" style={{ width: 460 }}>
            <div className="modal-header">
              <div className="modal-title">Agregar tarea no vinculada</div>
              <button className="modal-close" onClick={() => setModalNuevoItem(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group form-full" style={{ marginBottom: 14 }}>
                <label className="form-label">Título *</label>
                <input
                  className="form-input"
                  placeholder="Ej: Ir al juzgado a retirar cédula"
                  value={nuevoItem}
                  onChange={e => setNuevoItem(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Notas / instrucciones</label>
                <textarea
                  className="form-textarea"
                  placeholder="Información adicional, detalles, referencias..."
                  value={nuevoItemNotas}
                  onChange={e => setNuevoItemNotas(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModalNuevoItem(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={agregarItemLibre} disabled={!nuevoItem.trim() || agregandoItem}>
                {agregandoItem ? 'Agregando...' : 'Agregar tarea'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DETALLE DE ÍTEM */}
      {itemAbierto && (
        <div className="modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) { setItemAbierto(null); setEditandoNotasItem(false); } }} style={{ zIndex: 1200 }}>
          <div className="modal" style={{ width: 480 }}>
            <div className="modal-header">
              <div className="modal-title" style={{ flex: 1, paddingRight: 12 }}>{itemAbierto.descripcion}</div>
              <button className="modal-close" onClick={() => { setItemAbierto(null); setEditandoNotasItem(false); }}>✕</button>
            </div>
            <div className="modal-body">
              {/* Expediente vinculado */}
              {itemAbierto.tareas?.expedientes && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12, padding: '8px 12px', background: 'var(--surface2)', borderRadius: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>📁</span>
                    <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {itemAbierto.tareas.expedientes.caratula}
                    </span>
                  </div>
                  <button
                    className="btn btn-primary"
                    style={{ fontSize: 11, padding: '4px 12px', flexShrink: 0, height: 'auto' }}
                    onClick={() => {
                      setExpedienteDrawerExpId(itemAbierto.tareas.expedientes.id);
                      setExpedienteDrawerOpen(true);
                      setItemAbierto(null);
                      setEditandoNotasItem(false);
                    }}
                  >
                    Abrir expediente →
                  </button>
                </div>
              )}
              {/* Trámite vinculado */}
              {itemAbierto.tareas?.tramites && (
                <div style={{ fontSize: 12, color: 'var(--text4)', marginBottom: 12, padding: '8px 12px', background: 'var(--surface2)', borderRadius: 8 }}>
                  📋 {itemAbierto.tareas.tramites.nombre}
                </div>
              )}

              {/* Notas */}
              {editandoNotasItem ? (
                <div>
                  <textarea
                    className="form-textarea"
                    value={notasItemEdit}
                    onChange={e => setNotasItemEdit(e.target.value)}
                    rows={5}
                    autoFocus
                    placeholder="Notas, instrucciones, referencias..."
                    style={{ resize: 'vertical' }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                    <button className="btn btn-ghost" onClick={() => setEditandoNotasItem(false)}>Cancelar</button>
                    <button className="btn btn-primary" onClick={guardarNotasItem}>Guardar notas</button>
                  </div>
                </div>
              ) : itemAbierto.notas ? (
                <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.7, whiteSpace: 'pre-wrap', background: 'var(--surface2)', borderRadius: 8, padding: '12px 14px' }}>
                  {itemAbierto.notas}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--text4)', textAlign: 'center', padding: '20px 0' }}>Sin notas</div>
              )}

              {itemAbierto.completado && itemAbierto.completado_en && (
                <div style={{ fontSize: 11, color: '#10b981', marginTop: 12 }}>
                  ✓ Completada el {new Date(itemAbierto.completado_en).toLocaleDateString('es-AR')} a las {new Date(itemAbierto.completado_en).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
            </div>
            <div className="modal-footer">
              {!editandoNotasItem && esAdmin && (
                <button
                  className="btn btn-primary"
                  style={{ background: itemAbierto.notas ? 'var(--surface2)' : undefined, color: itemAbierto.notas ? 'var(--text2)' : undefined, border: itemAbierto.notas ? '1px solid var(--border2)' : undefined }}
                  onClick={() => { setNotasItemEdit(itemAbierto.notas || ''); setEditandoNotasItem(true); }}
                >
                  {itemAbierto.notas ? '✏️ Editar notas' : '📝 Agregar notas'}
                </button>
              )}
              <button className="btn btn-ghost" onClick={() => { setItemAbierto(null); setEditandoNotasItem(false); }}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL VINCULAR TAREA */}
      {modalVincular && (() => {
        const cerrarVincular = () => { setModalVincular(false); setTareaSeleccionadaVincular(null); setBusquedaVincular(''); setEntidadVincular(null); };
        const cambiarTab = (key) => { setTabVincular(key); setEntidadVincular(null); setTareaSeleccionadaVincular(null); setBusquedaVincular(''); };
        const labelTab = tabVincular === 'clientes' ? 'cliente' : tabVincular === 'expedientes' ? 'expediente' : 'trámite';
        return (
          <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && cerrarVincular()} style={{ zIndex: 1200 }}>
            <div className="modal" style={{ width: 700, maxWidth: '95vw' }}>
              <div className="modal-header">
                <div className="modal-title">Vincular tarea existente</div>
                <button className="modal-close" onClick={cerrarVincular}>✕</button>
              </div>

              {/* Tabs */}
              <div style={{ display: 'flex', borderBottom: '1px solid var(--border2)' }}>
                {[{ key: 'clientes', label: 'Clientes' }, { key: 'expedientes', label: 'Expedientes' }, { key: 'tramites', label: 'Trámites' }].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => cambiarTab(tab.key)}
                    style={{
                      flex: 1, padding: '11px 0', border: 'none', cursor: 'pointer',
                      background: tabVincular === tab.key ? 'var(--surface)' : 'var(--surface2)',
                      color: tabVincular === tab.key ? 'var(--blue)' : 'var(--text4)',
                      fontWeight: tabVincular === tab.key ? 600 : 400, fontSize: 13,
                      borderBottom: tabVincular === tab.key ? '2px solid var(--blue)' : '2px solid transparent',
                    }}
                  >{tab.label}</button>
                ))}
              </div>

              {/* Cuerpo: dos columnas */}
              <div style={{ display: 'flex', height: 380, overflow: 'hidden' }}>

                {/* Columna izquierda: buscador + lista de entidades */}
                <div style={{ width: 260, borderRight: '1px solid var(--border2)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border2)' }}>
                    <input
                      className="form-input"
                      placeholder={`Buscar ${labelTab}...`}
                      value={busquedaVincular}
                      onChange={e => { setBusquedaVincular(e.target.value); setEntidadVincular(null); setTareaSeleccionadaVincular(null); }}
                      autoFocus
                      style={{ margin: 0, fontSize: 12 }}
                    />
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto' }}>
                    {entidadesFiltradas.length === 0 ? (
                      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text4)', fontSize: 12 }}>
                        {busquedaVincular ? 'Sin resultados' : `No hay ${labelTab}s registrados`}
                      </div>
                    ) : (
                      entidadesFiltradas.map(entidad => {
                        const sel = entidadVincular?.id === entidad.id;
                        return (
                          <div
                            key={entidad.id}
                            onClick={() => { setEntidadVincular(sel ? null : entidad); setTareaSeleccionadaVincular(null); }}
                            style={{
                              padding: '10px 12px', cursor: 'pointer',
                              borderBottom: '1px solid var(--border2)',
                              background: sel ? 'var(--blue-light)' : 'transparent',
                              borderLeft: sel ? '3px solid var(--blue)' : '3px solid transparent',
                            }}
                          >
                            <div style={{ fontSize: 12, fontWeight: sel ? 600 : 400, color: 'var(--text)' }}>{entidad.nombre}</div>
                            {entidad.sub && <div style={{ fontSize: 10, color: 'var(--text4)', marginTop: 2 }}>{entidad.sub}</div>}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Columna derecha: tareas de la entidad seleccionada */}
                <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                  {!entidadVincular ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text4)', fontSize: 13, padding: 24, textAlign: 'center' }}>
                      Seleccioná un {labelTab} para ver sus tareas pendientes
                    </div>
                  ) : tareasEntidad.length === 0 ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text4)', fontSize: 13 }}>
                      Sin tareas pendientes
                    </div>
                  ) : (
                    <>
                      <div style={{ padding: '8px 14px', background: 'var(--surface2)', borderBottom: '1px solid var(--border2)', fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>
                        {entidadVincular.nombre}
                      </div>
                      {tareasEntidad.map(t => {
                        const sel = tareaSeleccionadaVincular?.id === t.id;
                        return (
                          <div
                            key={t.id}
                            onClick={() => setTareaSeleccionadaVincular(sel ? null : t)}
                            style={{
                              padding: '11px 14px', cursor: 'pointer',
                              borderBottom: '1px solid var(--border2)',
                              background: sel ? 'var(--blue-light)' : 'transparent',
                              borderLeft: sel ? '3px solid var(--blue)' : '3px solid transparent',
                            }}
                          >
                            <div style={{ fontSize: 13, fontWeight: sel ? 600 : 400, color: 'var(--text)' }}>{t.titulo}</div>
                            {t.expedientes && <div style={{ fontSize: 10, color: 'var(--text4)', marginTop: 2 }}>📁 {t.expedientes.caratula?.substring(0, 40)}</div>}
                            {t.tramites && <div style={{ fontSize: 10, color: 'var(--text4)', marginTop: 2 }}>📋 {t.tramites.nombre?.substring(0, 40)}</div>}
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                {tareaSeleccionadaVincular && (
                  <div style={{ fontSize: 12, color: 'var(--blue)', fontWeight: 500, flex: 1, marginRight: 8 }}>
                    ✓ {tareaSeleccionadaVincular.titulo}
                  </div>
                )}
                <button className="btn btn-ghost" onClick={cerrarVincular}>Cancelar</button>
                <button className="btn btn-primary" onClick={agregarItemVinculado} disabled={!tareaSeleccionadaVincular || agregandoItem}>
                  {agregandoItem ? 'Agregando...' : 'Agregar tarea'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* DRAWER EXPEDIENTE (desde ítem vinculado) */}
      {expedienteDrawerOpen && expedienteDrawerExpId && (
        <>
          <div className="drawer-overlay" onMouseDown={() => setExpedienteDrawerOpen(false)} style={{ zIndex: 1300 }} />
          <div className="drawer" style={{ zIndex: 1400 }}>
            <ExpedienteDetalle
              expedienteId={expedienteDrawerExpId}
              onClose={() => setExpedienteDrawerOpen(false)}
              onUpdate={() => {}}
            />
          </div>
        </>
      )}

      <ConfirmModal
        open={!!confirm}
        titulo={confirm?.titulo}
        mensaje={confirm?.mensaje}
        onConfirmar={confirm?.onConfirmar}
        onCancelar={() => setConfirm(null)}
      />

      {toast && <div className="toast">✓ {toast}</div>}
    </div>
  );
}
